/**
 * @file resolveAttachmentFileType.ts
 * @description The application's one file-type rule, classifying an attachment
 * from its filename and its declared content type. Consumers: every surface
 * showing an attachment — the attachments panel, communication log rows, the
 * files inside an expanded log entry, and the preview overlay, whose own
 * narrower classification derives from this so an icon and a preview can never
 * disagree.
 *
 * A known extension decides before the declared content type. `resolveContentType`
 * in `useAttachmentUpload.ts` maps no `cad` or `bim` extension, so those files
 * arrive declared `application/octet-stream`, and an inbound email keeps the
 * sender's declared type verbatim. For `cad` and `bim` the extension is the only
 * signal that exists, which is what fixes the order.
 */

import type { AttachmentFileType } from './index.types';

const FILE_TYPE_BY_EXTENSION: Record<string, AttachmentFileType> = {
    pdf: 'pdf',
    doc: 'word',
    docx: 'word',
    rtf: 'word',
    xls: 'spreadsheet',
    xlsx: 'spreadsheet',
    xlsm: 'spreadsheet',
    ods: 'spreadsheet',
    csv: 'csv',
    eml: 'email',
    msg: 'email',
    dwg: 'cad',
    dxf: 'cad',
    step: 'cad',
    stp: 'cad',
    iges: 'cad',
    igs: 'cad',
    ifc: 'bim',
    rvt: 'bim',
    nwd: 'bim',
    rfa: 'bim',
    png: 'image',
    jpg: 'image',
    jpeg: 'image',
    gif: 'image',
    webp: 'image',
    svg: 'image',
    bmp: 'image',
    tif: 'image',
    tiff: 'image',
    heic: 'image',
    txt: 'text',
    json: 'text',
    xml: 'text',
    md: 'text',
    log: 'text',
};

export const resolveAttachmentFileType = (
    fileName: string | undefined,
    declaredContentType: string | undefined,
): AttachmentFileType => {
    const normalizedName = (fileName ?? '').trim().toLowerCase();
    const lastDot = normalizedName.lastIndexOf('.');
    const extension = lastDot > 0 ? normalizedName.slice(lastDot + 1) : '';
    const knownExtension = FILE_TYPE_BY_EXTENSION[extension];
    if (knownExtension) return knownExtension;

    const contentType = (declaredContentType ?? '').split(';')[0].trim().toLowerCase();
    if (contentType.startsWith('image/')) return 'image';
    if (contentType === 'application/pdf') return 'pdf';
    if (contentType === 'text/csv') return 'csv';
    if (contentType === 'message/rfc822' || contentType === 'application/vnd.ms-outlook') return 'email';
    if (contentType === 'application/msword' || contentType.includes('wordprocessingml')) return 'word';
    if (contentType === 'application/vnd.ms-excel' || contentType.includes('spreadsheetml')) return 'spreadsheet';
    if (contentType === 'application/json' || contentType === 'application/xml' || contentType.startsWith('text/')) return 'text';

    return 'unknown';
};
